/**
 * Visible time on one page, for the admin Usage page.
 *
 * Counts only while the tab is visible: a page left open behind another window
 * is not being read. Time is taken in whole seconds, and the sub-second
 * remainder is kept, so a page visited in several stretches loses nothing.
 *
 * Every stretch of one visit carries the same `viewId`, which is how the
 * server counts a visit once however often the tab was hidden and shown.
 *
 * Pure and clock-injected, so it can be tested without a browser.
 */

export class PageTimer {
  readonly path: string;
  readonly viewId: string;
  private visibleSince: number | null;
  private bankedMs = 0;

  constructor(path: string, now: number, visible: boolean, viewId: string = newViewId()) {
    this.path = path;
    this.viewId = viewId;
    this.visibleSince = visible ? now : null;
  }

  /** The tab was hidden: stop counting. */
  hide(now: number): void {
    if (this.visibleSince !== null) {
      this.bankedMs += now - this.visibleSince;
      this.visibleSince = null;
    }
  }

  /** The tab is visible again: count from here. */
  show(now: number): void {
    this.visibleSince ??= now;
  }

  /** Hand over the whole seconds counted so far and start counting afresh. */
  take(now: number): number {
    const elapsed = this.bankedMs + (this.visibleSince === null ? 0 : now - this.visibleSince);
    if (this.visibleSince !== null) {
      this.visibleSince = now;
    }
    const seconds = Math.floor(Math.max(0, elapsed) / 1000);
    this.bankedMs = Math.max(0, elapsed) - seconds * 1000;
    return seconds;
  }
}

/** The longest stretch sent; the server caps there too. */
export const MAX_STRETCH_SECONDS = 60 * 60;

/**
 * The seconds to report for one stretch, or `null` to send nothing.
 *
 * Stretches under a second are not sent: they are redirects (`/` → `/ask`)
 * and React's development double-mount, not reading. Stretches over an hour
 * are sent as an hour, which is what the server keeps anyway — a tab left on
 * screen for a day would otherwise be refused outright.
 */
export function secondsToReport(seconds: number): number | null {
  if (seconds < 1) {
    return null;
  }
  return Math.min(seconds, MAX_STRETCH_SECONDS);
}

/**
 * A random id for one page visit.
 *
 * `crypto.randomUUID` exists only in a secure context; `getRandomValues` exists
 * everywhere, so a plain-http address still gets an id rather than a crash.
 */
export function newViewId(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
